'use client';

import React, { useMemo } from 'react';
import { useGetEntityDataByCategoriesQuery } from 'src/api/redux';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { formatCurrency } from 'src/utils/formatCurrency/formatCurrency';
import PreviewInlineBar from 'src/components/Profiles/shared/widgets/shared/PreviewInlineBar';

const PREVIEW_MAX_ITEMS = 2;

const CompactOpenPaymentsByCategoriesPreview: React.FC = () => {
  const widgetConfig = useWidgetConfig();
  const widget = widgetConfig?.widget ?? null;
  const { entityDetails } = useProfile();

  const { data, isLoading } = useGetEntityDataByCategoriesQuery(
    {
      providerId: entityDetails?.provider_id,
      company: widget?.entity?.id || undefined,
    },
    { skip: !entityDetails }
  );

  const items = useMemo(() => {
    if (!data?.results?.length) return [];

    return data.results
      .filter((item: { total_amount: number }) => item.total_amount > 0)
      .sort(
        (a: { total_amount: number }, b: { total_amount: number }) =>
          b.total_amount - a.total_amount
      )
      .slice(0, PREVIEW_MAX_ITEMS);
  }, [data]);

  if (isLoading || widgetConfig?.isLoading) {
    return <WidgetLoading data-testid="compact-open-payments-by-categories-preview-loading" aria-label="Loading open payments by categories" />;
  }

  if (items.length === 0) {
    return <WidgetEmpty data-testid="compact-open-payments-by-categories-preview-empty" />;
  }

  const maxValue = items[0].total_amount;

  return (
    <div
      className="flex flex-col gap-2"
      data-testid="compact-open-payments-by-categories-preview"
    >
      {items.map(
        (item: { category: string; total_amount: number }) => (
          <PreviewInlineBar
            key={item.category}
            label={item.category}
            value={item.total_amount}
            maxValue={maxValue}
            formattedValue={formatCurrency(item.total_amount)}
          />
        )
      )}
    </div>
  );
};

export default CompactOpenPaymentsByCategoriesPreview;
